import React, { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { DownloadSimple, CheckCircle } from "@phosphor-icons/react";
import { useT } from "@/context/LanguageContext";

export function LeadMagnet({ resources = [] }) {
  const { t } = useT();
  const featured = resources[0];
  const [form, setForm] = useState({ name: "", email: "", company: "" });
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!featured) return null;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post(`/resources/${featured.id}/download`, form);
      setDone(true);
      toast.success("Your guide is ready to download!");
      if (data.file_url) window.open(data.file_url, "_blank");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="bg-mist py-24 lg:py-32" data-testid="lead-magnet-section">
      <div className="mx-auto max-w-6xl px-5 lg:px-8">
        <div className="grid items-center gap-10 overflow-hidden rounded-2xl border border-slate-200 bg-white lg:grid-cols-2">
          <div className="relative h-64 lg:h-full">
            <img src={featured.cover_image} alt={featured.title} className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/60 to-transparent" />
          </div>
          <div className="p-8 lg:p-12">
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-teal">{t("leadMagnet.tag")}</p>
            <h2 className="mt-3 font-heading text-2xl font-semibold tracking-tight text-ink sm:text-3xl">
              {featured.title}
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">{featured.description}</p>

            {done ? (
              <div className="mt-7 flex items-center gap-3 rounded-lg bg-teal/10 p-5 text-teal-dark" data-testid="lead-magnet-success">
                <CheckCircle size={28} weight="fill" />
                <p className="text-sm font-medium">{t("leadMagnet.successTitle")}. {t("leadMagnet.success")}</p>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-7 space-y-3" data-testid="lead-magnet-form">
                <input
                  required placeholder={t("leadMagnet.namePh")} value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="lead-magnet-name"
                  className="w-full rounded-md border border-slate-200 px-4 py-3 text-sm outline-none focus:border-teal focus:ring-2 focus:ring-teal/30"
                />
                <input
                  required type="email" placeholder={t("leadMagnet.emailPh")} value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="lead-magnet-email"
                  className="w-full rounded-md border border-slate-200 px-4 py-3 text-sm outline-none focus:border-teal focus:ring-2 focus:ring-teal/30"
                />
                <input
                  placeholder={t("leadMagnet.companyPh")} value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  data-testid="lead-magnet-company"
                  className="w-full rounded-md border border-slate-200 px-4 py-3 text-sm outline-none focus:border-teal focus:ring-2 focus:ring-teal/30"
                />
                <button
                  type="submit" disabled={loading}
                  data-testid="lead-magnet-submit"
                  className="flex w-full items-center justify-center gap-2 rounded-md bg-ink px-6 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-ink-800 disabled:opacity-60"
                >
                  <DownloadSimple size={18} weight="bold" />
                  {loading ? t("leadMagnet.preparing") : t("leadMagnet.submit")}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
